import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import AnalyticsCharts from '../components/AnalyticsCharts';
import DateRangeSelector from '../components/DateRangeSelector';

const AnalyticsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const AnalyticsHeader = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;

  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const AnalyticsTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    text-decoration: underline;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const MetricCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border: 1px solid #e2e8f0;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  color: #2d3748;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${props => props.color || props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  color: #718096;
  font-size: 0.9rem;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #fed7d7;
  color: #742a2a;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
`;

const SuccessMessage = styled.div`
  background-color: #c6f6d5;
  color: #22543d;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
`;

const RefreshButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.secondary};
  }

  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #2d3748;
  margin: 2rem 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;
`;

const DataTable = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border: 1px solid #e2e8f0;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '2fr 1fr 1fr 1fr'};
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  &:first-child {
    background: #f7fafc;
    font-weight: bold;
    color: #2d3748;
  }

  &:not(:first-child):hover {
    background: #f9fafb;
  }
`;

const IndexingForm = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border: 1px solid #e2e8f0;
  margin-top: 2rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: ${props => props.theme.colors.secondary};
  }

  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
  }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'good': return '#48bb78';
      case 'needs-improvement': return '#ed8936';
      case 'poor': return '#f56565';
      default: return '#cbd5e0';
    }
  }};
  margin-right: 0.5rem;
`;

function AdminAnalytics() {
  const [data, setData] = useState({
    overview: null,
    searchConsole: null,
    webVitals: null,
    status: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [indexingUrl, setIndexingUrl] = useState('');
  const [indexingLoading, setIndexingLoading] = useState(false);

  // New state for date range and charts
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    label: '7 Days'
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { 'Authorization': `Bearer ${token}` };
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/admin';
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const headers = getAuthHeaders();

      // Fetch all data in parallel
      const [overviewRes, searchConsoleRes, webVitalsRes, statusRes] = await Promise.all([
        axios.get('/api/admin/analytics/overview', { headers }),
        axios.get('/api/admin/analytics/search-console', { headers }),
        axios.get('/api/admin/analytics/web-vitals', { headers }),
        axios.get('/api/admin/analytics/status', { headers })
      ]);

      setData({
        overview: overviewRes.data,
        searchConsole: searchConsoleRes.data,
        webVitals: webVitalsRes.data,
        status: statusRes.data
      });
    } catch (error) {
      handleAuthError(error);
      setError('Failed to load analytics data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array since getAuthHeaders and handleAuthError are stable

  // New function to fetch analytics data for charts with date range
  const fetchAnalyticsData = useCallback(async (startDate, endDate) => {
    setChartLoading(true);
    setError('');

    try {
      const headers = getAuthHeaders();

      // Fetch analytics data with date range parameters
      const response = await axios.get('/api/admin/analytics/overview', {
        headers,
        params: { startDate, endDate }
      });

      setAnalyticsData(response.data?.data);
    } catch (error) {
      handleAuthError(error);
      setError('Failed to load chart data: ' + (error.response?.data?.error || error.message));
    } finally {
      setChartLoading(false);
    }
  }, []);

  // Handler for date range changes
  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
    fetchAnalyticsData(newDateRange.startDate, newDateRange.endDate);
  }, [fetchAnalyticsData]);

  const handleIndexingRequest = async (e) => {
    e.preventDefault();
    if (!indexingUrl) return;

    setIndexingLoading(true);
    setError('');
    setSuccess('');

    try {
      const headers = getAuthHeaders();
      await axios.post('/api/admin/analytics/request-indexing',
        { url: indexingUrl },
        { headers }
      );

      setSuccess(`Successfully submitted ${indexingUrl} for indexing`);
      setIndexingUrl('');
    } catch (error) {
      handleAuthError(error);
      setError('Failed to request indexing: ' + (error.response?.data?.error || error.message));
    } finally {
      setIndexingLoading(false);
    }
  };

  const getPerformanceStatus = (score) => {
    if (score >= 90) return 'good';
    if (score >= 50) return 'needs-improvement';
    return 'poor';
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.location.href = '/admin';
      return;
    }

    fetchData();
    // Also fetch initial chart data for 7 days
    fetchAnalyticsData(dateRange.startDate, dateRange.endDate);
  }, [fetchData, fetchAnalyticsData, dateRange.startDate, dateRange.endDate]);

  return (
    <AnalyticsContainer>
      <AnalyticsHeader>
        <BackLink to="/admin">← Back to Admin</BackLink>
        <AnalyticsTitle>📊 Analytics Dashboard</AnalyticsTitle>
        <p>Google Analytics, Search Console & Performance Monitoring</p>
      </AnalyticsHeader>

      <RefreshButton
        onClick={fetchData}
        disabled={loading}
      >
        {loading ? <LoadingSpinner /> : '🔄'}
        {loading ? 'Loading...' : 'Refresh Data'}
      </RefreshButton>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {/* Date Range Selector */}
      <SectionTitle>📅 Analytics Time Period</SectionTitle>
      <DateRangeSelector
        onDateRangeChange={handleDateRangeChange}
        isLoading={chartLoading}
      />

      {/* Interactive Charts */}
      <SectionTitle>📊 Analytics Visualization - {dateRange.label}</SectionTitle>
      <AnalyticsCharts
        analyticsData={analyticsData}
        isLoading={chartLoading}
        error={error}
      />

      {/* Real-time Metrics */}
      <SectionTitle>📈 Real-time Overview</SectionTitle>
      <GridContainer>
        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <CardTitle>👥 Active Users</CardTitle>
          <MetricValue color="#48bb78">
            {data.overview?.data?.realtime?.activeUsers || 0}
          </MetricValue>
          <MetricLabel>Currently on site</MetricLabel>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <CardTitle>📄 Page Views (7 days)</CardTitle>
          <MetricValue color="#4299e1">
            {formatNumber(data.overview?.data?.pageViews?.totalPageViews)}
          </MetricValue>
          <MetricLabel>{formatNumber(data.overview?.data?.pageViews?.totalSessions)} sessions</MetricLabel>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <CardTitle>🔍 Search Impressions</CardTitle>
          <MetricValue color="#9f7aea">
            {formatNumber(data.searchConsole?.data?.analytics?.totals?.impressions)}
          </MetricValue>
          <MetricLabel>{formatNumber(data.searchConsole?.data?.analytics?.totals?.clicks)} clicks</MetricLabel>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <CardTitle>📊 Avg Performance Score</CardTitle>
          <MetricValue color="#ed8936">
            {data.webVitals?.data?.overallStats?.averagePerformanceScore || 0}
          </MetricValue>
          <MetricLabel>
            <StatusDot status={getPerformanceStatus(data.webVitals?.data?.overallStats?.averagePerformanceScore)} />
            {data.webVitals?.data?.overallStats?.analyzedPages || 0} pages analyzed
          </MetricLabel>
        </MetricCard>
      </GridContainer>

      {/* Top Pages */}
      {data.overview?.data?.topPages && (
        <>
          <SectionTitle>📈 Top Pages (7 days)</SectionTitle>
          <DataTable>
            <TableRow columns="3fr 1fr 1fr 1fr">
              <div>Page</div>
              <div>Views</div>
              <div>Users</div>
              <div>Avg Duration</div>
            </TableRow>
            {data.overview.data.topPages.slice(0, 10).map((page, index) => (
              <TableRow key={index} columns="3fr 1fr 1fr 1fr">
                <div>{page.title || page.page}</div>
                <div>{formatNumber(page.pageViews)}</div>
                <div>{formatNumber(page.users)}</div>
                <div>{Math.round(page.avgSessionDuration || 0)}s</div>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}

      {/* Top Search Queries */}
      {data.searchConsole?.data?.topQueries && (
        <>
          <SectionTitle>🔍 Top Search Queries</SectionTitle>
          <DataTable>
            <TableRow columns="2fr 1fr 1fr 1fr 1fr">
              <div>Query</div>
              <div>Clicks</div>
              <div>Impressions</div>
              <div>CTR</div>
              <div>Position</div>
            </TableRow>
            {data.searchConsole.data.topQueries.slice(0, 10).map((query, index) => (
              <TableRow key={index} columns="2fr 1fr 1fr 1fr 1fr">
                <div>{query.query}</div>
                <div>{formatNumber(query.clicks)}</div>
                <div>{formatNumber(query.impressions)}</div>
                <div>{query.ctr}%</div>
                <div>{query.position}</div>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}

      {/* Web Vitals */}
      {data.webVitals?.data?.pageResults && (
        <>
          <SectionTitle>⚡ Core Web Vitals</SectionTitle>
          <DataTable>
            <TableRow columns="2fr 1fr 1fr 1fr 1fr">
              <div>Page</div>
              <div>Performance</div>
              <div>Accessibility</div>
              <div>SEO</div>
              <div>Status</div>
            </TableRow>
            {data.webVitals.data.pageResults.map((page, index) => (
              <TableRow key={index} columns="2fr 1fr 1fr 1fr 1fr">
                <div>{page.url.replace('https://butdidyoudie.life', '') || '/'}</div>
                <div>{page.scores.performance}</div>
                <div>{page.scores.accessibility}</div>
                <div>{page.scores.seo}</div>
                <div>
                  <StatusDot status={getPerformanceStatus(page.scores.performance)} />
                  {page.assessment.category}
                </div>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}

      {/* Request Indexing */}
      <SectionTitle>🚀 Request Google Indexing</SectionTitle>
      <IndexingForm>
        <form onSubmit={handleIndexingRequest}>
          <FormInput
            type="url"
            placeholder="https://butdidyoudie.life/page-url"
            value={indexingUrl}
            onChange={(e) => setIndexingUrl(e.target.value)}
            required
          />
          <SubmitButton type="submit" disabled={indexingLoading}>
            {indexingLoading ? 'Submitting...' : 'Request Indexing'}
          </SubmitButton>
        </form>
      </IndexingForm>

      {/* System Status */}
      {data.status && (
        <>
          <SectionTitle>⚙️ System Status</SectionTitle>
          <GridContainer>
            <MetricCard>
              <CardTitle>
                <StatusDot status={data.status.data.googleAnalytics.connected ? 'good' : 'poor'} />
                Google Analytics
              </CardTitle>
              <MetricValue color={data.status.data.googleAnalytics.connected ? '#48bb78' : '#f56565'}>
                {data.status.data.googleAnalytics.connected ? 'Connected' : 'Disconnected'}
              </MetricValue>
              {data.status.data.googleAnalytics.error && (
                <MetricLabel style={{color: '#f56565'}}>
                  {data.status.data.googleAnalytics.error}
                </MetricLabel>
              )}
            </MetricCard>

            <MetricCard>
              <CardTitle>
                <StatusDot status={data.status.data.searchConsole.connected ? 'good' : 'poor'} />
                Search Console
              </CardTitle>
              <MetricValue color={data.status.data.searchConsole.connected ? '#48bb78' : '#f56565'}>
                {data.status.data.searchConsole.connected ? 'Connected' : 'Disconnected'}
              </MetricValue>
              {data.status.data.searchConsole.error && (
                <MetricLabel style={{color: '#f56565'}}>
                  {data.status.data.searchConsole.error}
                </MetricLabel>
              )}
            </MetricCard>

            <MetricCard>
              <CardTitle>
                <StatusDot status={data.status.data.pageSpeedInsights.connected ? 'good' : 'poor'} />
                PageSpeed Insights
              </CardTitle>
              <MetricValue color={data.status.data.pageSpeedInsights.connected ? '#48bb78' : '#f56565'}>
                {data.status.data.pageSpeedInsights.connected ? 'Connected' : 'Disconnected'}
              </MetricValue>
              {data.status.data.pageSpeedInsights.error && (
                <MetricLabel style={{color: '#f56565'}}>
                  {data.status.data.pageSpeedInsights.error}
                </MetricLabel>
              )}
            </MetricCard>
          </GridContainer>
        </>
      )}
    </AnalyticsContainer>
  );
}

export default AdminAnalytics;